/**
 * A "Choose …" scope section on one line: the heading, its (i), the control and the action.
 *
 * Owner request, 2026-09-30, about Price and markdown's "Choose a cycle run": "on the same row,
 * the title, then the dropdown to its right, then the button to its right, all in one line", with
 * no visible field label and no explanatory paragraph, and "the last comment applies to all the
 * 'choose' sections". `docs/adr/9036` is the record.
 *
 * It is a layout and nothing else. It does not parse a scope, read a run or write the URL: each
 * chapter's picker keeps doing those. What it fixes is the shape, once:
 *
 * - the heading and the (i) (`AboutPopover`, the same one `Panel` draws) come first, and the
 *   explanation that used to sit under the control goes behind the (i);
 * - the controls follow on the same row, which wraps on a narrow width instead of scrolling
 *   sideways (logical properties only, G6);
 * - the section is named by its heading, as `Panel`'s is. The control keeps its own accessible
 *   name through its label, drawn visually hidden (`Select`'s `labelHidden`), so no name is lost;
 * - the children are inside an `Enclosure`, so an `AsyncRegion` in the row renders its loading,
 *   empty and error states inline, as it does inside a `Panel`.
 *
 * It is closed by the same hairline as a `Panel` band, so it reads as one of the page's sections.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Enclosure } from '../primitives/enclosure';
import { AboutPopover } from './panel';

export interface ScopeChooserProps {
  /** ICU key for the heading, e.g. "Choose a cycle run". */
  readonly titleKey: string;
  /** The explanation, behind the (i): one paragraph per key, in order. */
  readonly aboutKey?: string | readonly string[];
  readonly values?: Readonly<Record<string, string | number>>;
  /** The row's controls: the select, the action, and any short inline note after them. */
  readonly children: ReactNode;
}

/**
 * Width for the control inside the row: it asks for 16 rem and grows to 36, but never more than the row it stands in. A
 * `min-w-[16rem]` floor would be wider than a phone's row (the page is 334 px wide at 390 px, and the heading has taken part of
 * it), and a control wider than its row scrolls the page sideways (measured 2026-11-03 on compacting-moves, compacting-report
 * and run-scopes). The row's own basis (below) is what wraps the controls under the heading; this one lets a control that
 * has nowhere left to wrap to give way.
 */
export const SCOPE_CHOOSER_CONTROL = 'min-w-0 max-w-xl flex-[1_1_16rem]';

export function ScopeChooser({ titleKey, aboutKey, values, children }: ScopeChooserProps): React.JSX.Element {
  const { t } = useTranslation();
  const headingId = useId();
  const title = t(titleKey, values ?? {});
  const about = aboutKey === undefined ? [] : typeof aboutKey === 'string' ? [aboutKey] : aboutKey;
  return (
    <section
      aria-labelledby={headingId}
      data-scope-chooser=""
      data-tour="scope-chooser"
      className="border-hairline flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 border-b pb-6"
    >
      <div className="flex shrink-0 items-center gap-2">
        <h2 id={headingId} className="text-section text-ink-primary font-display uppercase">
          {title}
        </h2>
        {about.length > 0 && <AboutPopover aboutKeys={about} title={title} {...(values === undefined ? {} : { values })} />}
      </div>
      {/* A basis, not `flex-1` (basis 0): with no basis the row never wraps under the heading, it only shrinks. */}
      <div className="flex min-w-0 flex-[1_1_16rem] flex-wrap items-center gap-3">
        <Enclosure>{children}</Enclosure>
      </div>
    </section>
  );
}
